import React, { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useFetchTable } from "@hooks/useFetchDataTable";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { ActionUploadDownloadIterface } from "@components/ModalDownloadUploadCst/types";
import Table from "@components/Table";
import { ServicesURL } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import { downloadFile, uploadFile } from "@utils/utilsApi";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import { ActionsType } from "src/interfaces/Common";
import { KeyURL, MethodType } from "src/services/type";
import { fetcher } from "src/services/config";
import useSWRMutation from "swr/mutation";
import AnimationFadeIn from "@components/AnimationFadeIn";
import CustomRowAction from "@components/Table/components/CustomRowAction";
import { useTheme } from "@mui/material";
import { useAuth } from "@context/auth";
import { useFilters } from "@context/filters";

interface AccountingGoodReceiptProps {
  odaSapCode: string | undefined;
}

const AccountingGoodReceipt: React.FC<AccountingGoodReceiptProps> = ({
  odaSapCode,
}) => {
  const { id } = useParams();
  const { t } = useTranslation(["accounting-page"]);
  const { t: tOptions } = useTranslation(["input"]);
  const theme = useTheme();
  const navigate = useNavigate();
  const [openUpload, setOpenUpload] = useState<ActionUploadDownloadIterface>();
  const [idGoodReceiptDeleted, setIdGoodReceiptDeleted] = useState<string>();
  const [idCostOfReceipt, setIdCostOfReceipt] = useState<string>();
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [openToast, setOpenToast] = useState<{
    s: StatusEnum;
    message: string;
  }>();
  const location = useLocation();
  const { user } = useAuth();
  const { filters, setFilters } = useFilters();
  const filterId = `${user?.id_user}_${PagesID["projects.cost-goods-receipts"]}`;

  useEffect(() => {
    if (odaSapCode) {
      setFilters(filterId, {
        ...filters[filterId],
        oda_sap_code: odaSapCode,
      });
    } else {
      const newFilters = { ...filters[filterId] };
      delete newFilters.oda_sap_code;
      setFilters(filterId, newFilters);
    }
  }, [odaSapCode, filterId]);

  /*Function to call api to get all Goods Receipt*/
  const { columns, dataTable, actionsTopToolbar, actionForRow, mutate } =
    useFetchTable(
      `${ServicesURL.getAllGoodsReceipts}?corporateId=${id}`, //${odaSapCode ? `&oda_sap_code=${odaSapCode}` : ""}
      PagesID["projects.cost-goods-receipts"],
      filters[filterId],
    );

  /*Trigger to delete a detail receipt*/
  const { trigger: triggerDeleteGoodReceipt } = useSWRMutation(
    `${ServicesURL.postGoodsReceipts.replaceAll(":costId", String(idCostOfReceipt))}/${idGoodReceiptDeleted}?corporateId=${id}`,
    (url: string) => {
      return fetcher<any>(url, MethodType.DELETE);
    },
  );

  /*Function to handle the delete acition*/
  const handleDelete = React.useCallback(async () => {
    if (idGoodReceiptDeleted) {
      setOpenToast({ s: StatusEnum.LOADING, message: "loading" });
      try {
        await triggerDeleteGoodReceipt();
        const newData = [
          ...dataTable.output.filter(
            (el: any) => el.id !== idGoodReceiptDeleted,
          ),
        ];
        mutate && mutate({ ...dataTable, ...{ output: newData } });
        setOpenToast({ s: StatusEnum.OK, message: "" });
      } catch (error) {
        setIdGoodReceiptDeleted(undefined);
        setIdCostOfReceipt(undefined);
        setOpenToast(undefined);
        console.error(error);
      }
    }
  }, [dataTable, idGoodReceiptDeleted, mutate, triggerDeleteGoodReceipt]);

  /*Formatting the data from server*/
  const dataFormat = useMemo(() => {
    if (!dataTable?.output) return [];

    return dataTable.output.map((el: any) => {
      if (el.customer) {
        el.customer.label = el.customer?.business_name || "-";
      }

      return el;
    });
  }, [dataTable?.output]);

  useEffect(() => {
    if (!idGoodReceiptDeleted) return;
  }, [idGoodReceiptDeleted]);

  const filteredColumns = useMemo(() => {
    if (columns) {
      return columns.map((el) => {
        return { ...el };
      });
    }
  }, [columns]);

  const configurationActionForRow: ActionsType[] = React.useMemo(() => {
    return actionForRow?.map((action: any) => {
      switch (action.type) {
        case "delete": {
          return {
            ...action,
            callBack: (row: any) => {
              setIdGoodReceiptDeleted(row.gr_id);
              setIdCostOfReceipt(row.extra_data.cost_id);
              setOpenModalDelete(true);
            },
          };
        }
        case "edit": {
          return {
            ...action,
            callBack: (data: any) => {
              navigate(
                `${location.pathname}/accounting-good-receipt/GR/${data.extra_data.project_id}/${data.extra_data.cost_model_item_id}/${data.extra_data.cost_id}/${data.gr_id}`,
              );
            },
          };
        }
        default:
          return action;
      }
    });
  }, [actionForRow, navigate]);

  const customRenderRowActions = ({ row }: { row: any }): React.ReactNode => {
    /*Rimuovo l'id da row per far si che nella callback della delete abbia
      poi l'intera riga e non solo l'id. Questo perchè nella delete mi serve anche
      il campo extra_data.cost_id
    */
    const originalCopy = { ...row.original };
    const modifiedOriginal = { ...originalCopy, gr_id: originalCopy.id };
    delete modifiedOriginal.id;
    const newRow = { ...row, original: modifiedOriginal };

    let configRowNew: any = configurationActionForRow && [
      ...configurationActionForRow,
    ];

    // Disabilito delete se non deletable
    configRowNew = configRowNew.map((el: ActionsType) => {
      if (el.type === "delete" && !modifiedOriginal?.deletable) {
        return { ...el, disabled: true };
      }
      return { ...el };
    });

    return [
      <CustomRowAction
        key={row.id}
        row={newRow}
        configurationActionForRow={configRowNew}
      />,
    ];
  };

  const actionsToolbar: ActionsType[] | undefined = React.useMemo(() => {
    return actionsTopToolbar
      ?.filter((action) => action.type !== "add")
      .map((action: ActionsType) => {
        switch (action.type) {
          case "upload": {
            const actionModal: ActionUploadDownloadIterface = {
              ...action,
              ...{
                service: `${ServicesURL[action.service as KeyURL]}?corporate_id=1`,
                types: ["upload", "download", "template"],
                onDownload: (el) =>
                  downloadFile(
                    `${ServicesURL[action.service as KeyURL]}/download${
                      el ? `/${el}` : ""
                    }?corporate_id=1&type=LEARNER` || "",
                  ),
                onUpload: (formData: FormData) => {
                  return uploadFile(
                    `${ServicesURL[action.service as KeyURL]}?corporate_id=1`,
                    formData,
                  );
                },
              },
            };
            return {
              ...action,
              ...{ callBack: () => setOpenUpload(actionModal) },
            };
          }
          case "visibility": {
            const actionModal: ActionUploadDownloadIterface = {
              ...action,
              ...{
                service: `${
                  ServicesURL[action.service as KeyURL]
                }?corporate_id=1`,
                types: ["upload", "download", "template"],
                onDownload: (el) =>
                  downloadFile(
                    `${ServicesURL[action.service as KeyURL]}/?corporate_id=1` ||
                      "",
                  ),
                onUpload: (formData: FormData) =>
                  uploadFile(
                    `${ServicesURL[action.service as KeyURL]}?corporate_id=1`,
                    formData,
                  ),
              },
            };
            return {
              ...action,
              ...{ callBack: () => setOpenUpload(actionModal) },
            };
          }
          default:
            return action;
        }
      });
  }, [actionsTopToolbar]);

  const renderCell = (cel: any, value: any, row: any) => {
    return value
      ? tOptions("options.switch_key.true")
      : tOptions("options.switch_key.false");
  };

  return (
    <AnimationFadeIn>
      <Table
        id={PagesID["projects.cost-goods-receipts"]}
        data={dataFormat || []}
        columns={filteredColumns || []}
        configurationTopToolbar={actionsToolbar}
        renderRowActions={customRenderRowActions}
        totalPages={dataTable?.metadata?.total_elements}
        modalTitle={t("hero-title")?.replace("-", "") || ""}
        renderCell={renderCell}
      />
      <ModalConfirmsCst
        open={!!openModalDelete}
        title={t("delete-item", { ns: "modal" })}
        description={t("confirm-delete", { ns: "modal" })}
        routeToSamePage={true}
        onCallBackClosed={() => {
          setOpenModalDelete(!openModalDelete);
          setOpenToast(undefined);
        }}
        onActionConfirmed={() => handleDelete()}
        status={openToast}
      />
    </AnimationFadeIn>
  );
};

export default AccountingGoodReceipt;
